/**
 * Teacher view: who has practised (Quick Checks) and who has attempted
 * the interactive tests, per subject/year.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, CheckCircle2, Medal, ClipboardCheck, BookOpenCheck } from 'lucide-react';
import { Badge } from '@/components/ui/badge';

const API = process.env.REACT_APP_BACKEND_URL;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('authToken')}` });

const BADGE_STYLES = {
  gold: 'bg-yellow-100 text-yellow-800 border-yellow-400',
  silver: 'bg-gray-100 text-gray-700 border-gray-400',
  bronze: 'bg-orange-100 text-orange-800 border-orange-400',
};

export default function QuickCheckProgressPage() {
  const [pairs, setPairs] = useState([]);
  const [selected, setSelected] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API}/api/quick-check/teacher/subjects`, { headers: authHeaders() })
      .then((r) => r.json())
      .then((d) => {
        const list = d.subjects || [];
        setPairs(list);
        if (list.length) setSelected(`${list[0].subject}|${list[0].year_level}`);
      })
      .finally(() => setLoading(false));
  }, []);

  const [subject, yearLevel] = selected ? selected.split('|') : [null, null];

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="quick-check-progress-page">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <BookOpenCheck className="h-6 w-6 text-emerald-600" />
          Quick Check Practice
        </h1>
        <p className="text-sm text-gray-500 mt-1">See who has practised the chapter Quick Checks before their tests. Graded activities and tests live under Activities & Tests.</p>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-emerald-600" /></div>
      ) : !pairs.length ? (
        <div className="text-center py-16 text-gray-500">No Quick Check content found for your classes yet.</div>
      ) : (
        <>
          <div className="mb-4 flex items-center gap-3">
            <label className="text-sm font-medium text-gray-700">Class:</label>
            <select
              value={selected}
              onChange={(e) => setSelected(e.target.value)}
              className="border rounded-md px-3 py-2 text-sm bg-white"
              data-testid="subject-year-select"
            >
              {pairs.map((p) => (
                <option key={`${p.subject}|${p.year_level}`} value={`${p.subject}|${p.year_level}`}>
                  Year {p.year_level} {p.subject}
                </option>
              ))}
            </select>
          </div>

          {subject && (
            <div>
                <PracticeGrid subject={subject} yearLevel={yearLevel} />
            </div>
          )}
        </>
      )}
    </div>
  );
}

function PracticeGrid({ subject, yearLevel }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetch(`${API}/api/quick-check/teacher/overview?subject=${encodeURIComponent(subject)}&year_level=${yearLevel}`, { headers: authHeaders() })
      .then((r) => r.json())
      .then(setData)
      .finally(() => setLoading(false));
  }, [subject, yearLevel]);

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-emerald-600" /></div>;
  if (!data?.students?.length) return <div className="py-12 text-center text-gray-500">No students found for this class.</div>;
  if (!data?.chapters?.length) return <div className="py-12 text-center text-gray-500">No Quick Check sets for this subject/year yet.</div>;

  const practised = data.students.filter((s) => data.grid[s.id] && Object.keys(data.grid[s.id]).length > 0).length;

  return (
    <div className="mt-4">
      <div className="flex gap-4 mb-3 text-sm">
        <Badge variant="outline" className="bg-emerald-50 text-emerald-800 border-emerald-300">
          {practised}/{data.students.length} students have practised
        </Badge>
        <span className="text-gray-500 flex items-center gap-2">
          <Medal className="h-4 w-4 text-yellow-500" /> gold = all correct · silver = mostly · bronze = completed
        </span>
      </div>
      <div className="overflow-x-auto border rounded-lg bg-white">
        <table className="w-full text-sm" data-testid="practice-grid">
          <thead>
            <tr className="bg-gray-50 border-b">
              <th className="text-left px-3 py-2 font-semibold sticky left-0 bg-gray-50 min-w-[180px]">Student</th>
              {data.chapters.map((c) => (
                <th key={c.id} className="px-2 py-2 font-medium text-gray-600 min-w-[90px]" title={c.title}>
                  <div className="text-xs truncate max-w-[140px] mx-auto">{c.title.replace('Quick Check: ', '').replace('Test Prep: ', 'Prep: ')}</div>
                  <div className="text-[10px] text-gray-400">{c.total_questions} Qs</div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.students.map((s, i) => (
              <tr key={s.id} className={i % 2 ? 'bg-gray-50/50' : ''}>
                <td className="px-3 py-2 font-medium sticky left-0 bg-inherit whitespace-nowrap">{s.name}</td>
                {data.chapters.map((c) => {
                  const cell = data.grid?.[s.id]?.[c.id];
                  if (!cell) return <td key={c.id} className="text-center text-gray-300">—</td>;
                  return (
                    <td key={c.id} className="text-center px-2 py-1">
                      <Badge variant="outline" className={cell.badge ? BADGE_STYLES[cell.badge] : 'bg-blue-50 text-blue-700 border-blue-200'}>
                        {cell.badge ? cell.badge : `${cell.got_it}✓`}
                      </Badge>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function TestsTable({ subject, yearLevel }) {
  const [exams, setExams] = useState([]);
  const [examId, setExamId] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetch(`${API}/api/interactive-exams/teacher-list/${encodeURIComponent(subject)}/${yearLevel}`, { headers: authHeaders() })
      .then((r) => r.json())
      .then((d) => {
        const list = d.exams || [];
        setExams(list);
        setExamId(list.length ? list[0].id : '');
        if (!list.length) setLoading(false);
      });
  }, [subject, yearLevel]);

  const loadOverview = useCallback(() => {
    if (!examId) return;
    setLoading(true);
    fetch(`${API}/api/interactive-exams/teacher/${encodeURIComponent(subject)}/${yearLevel}?exam_id=${examId}`, { headers: authHeaders() })
      .then((r) => r.json())
      .then(setData)
      .finally(() => setLoading(false));
  }, [examId, subject, yearLevel]);

  useEffect(() => { loadOverview(); }, [loadOverview]);

  if (!exams.length && !loading) return <div className="py-12 text-center text-gray-500">No interactive tests for this subject/year yet.</div>;

  const attempted = data?.students?.filter((s) => s.attempts_used > 0).length || 0;
  const avg = (() => {
    const scores = (data?.students || []).filter((s) => s.best_percentage !== null).map((s) => s.best_percentage);
    return scores.length ? Math.round(scores.reduce((a, b) => a + b, 0) / scores.length) : null;
  })();

  return (
    <div className="mt-4">
      <div className="flex flex-wrap items-center gap-3 mb-3">
        <select
          value={examId}
          onChange={(e) => setExamId(e.target.value)}
          className="border rounded-md px-3 py-2 text-sm bg-white max-w-md"
          data-testid="exam-select"
        >
          {exams.map((ex) => <option key={ex.id} value={ex.id}>{ex.title}</option>)}
        </select>
        {data && (
          <>
            <Badge variant="outline" className="bg-emerald-50 text-emerald-800 border-emerald-300">
              {attempted}/{data.students?.length || 0} attempted
            </Badge>
            {avg !== null && (
              <Badge variant="outline" className="bg-sky-50 text-sky-800 border-sky-300">Class average: {avg}%</Badge>
            )}
          </>
        )}
      </div>
      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-emerald-600" /></div>
      ) : (
        <div className="overflow-x-auto border rounded-lg bg-white">
          <table className="w-full text-sm" data-testid="tests-table">
            <thead>
              <tr className="bg-gray-50 border-b">
                <th className="text-left px-3 py-2 font-semibold">Student</th>
                <th className="px-3 py-2 font-semibold">Attempts</th>
                <th className="px-3 py-2 font-semibold">Best Score</th>
                <th className="px-3 py-2 font-semibold">Best %</th>
                <th className="px-3 py-2 font-semibold">Status</th>
              </tr>
            </thead>
            <tbody>
              {(data?.students || []).map((s, i) => (
                <tr key={s.student_id} className={i % 2 ? 'bg-gray-50/50' : ''}>
                  <td className="px-3 py-2 font-medium whitespace-nowrap">{s.name}</td>
                  <td className="px-3 py-2 text-center">{s.attempts_used}/{data.exam.max_attempts}</td>
                  <td className="px-3 py-2 text-center">{s.best_score !== null ? `${s.best_score}/${data.exam.total_marks}` : '—'}</td>
                  <td className="px-3 py-2 text-center">
                    {s.best_percentage !== null ? (
                      <span className={s.best_percentage >= 50 ? 'text-emerald-700 font-semibold' : 'text-red-600 font-semibold'}>
                        {s.best_percentage}%
                      </span>
                    ) : '—'}
                  </td>
                  <td className="px-3 py-2 text-center">
                    {s.attempts_used > 0 ? (
                      <CheckCircle2 className="h-4 w-4 text-emerald-600 inline" />
                    ) : (
                      <span className="text-gray-400 text-xs">Not started</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
